import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TArchiveTicketValidation,
  ArchiveTicketValidationSchema,
  useArchiveTicketMutation,
} from '../../../api/archive-ticket.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_SINGLE_TICKET_QUERY_KEY } from '../../../api/get-single-ticket.api';
import { GET_TICKET_ACTIVITY_QUERY_KEY } from '../../../../ticket-activity/api/get-list-ticket-activity.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

export interface IArchiveTicketModalProps {
  spaceId: string;
  ticketId: string;
  onStart?: () => Promise<void> | void;
  onSuccess?: () => Promise<void> | void;
}

export function useArchiveTicketModal({ spaceId, ticketId, onStart, onSuccess }: IArchiveTicketModalProps) {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();

  const mutationArchiveTicket = useArchiveTicketMutation({
    mutationConfig: {
      onMutate: async (): Promise<void> => {
        if (onStart) {
          await onStart();
        }
      },
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error archive ticket, please try again';
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: async () => {
        showToast({ message: 'Ticket archived successfully!', type: 'success' });
        modalActions.closeModal('ArchiveTicketModal');
        form.reset();

        // Invalidate queries
        if (spaceId && ticketId) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [GET_SINGLE_TICKET_QUERY_KEY, { ticketId }],
            }),
            queryClient.invalidateQueries({
              queryKey: [GET_TICKET_ACTIVITY_QUERY_KEY, { ticketId }],
            }),
          ]);
        }

        // Call the onSuccess callback if provided (e.g., to update form in parent modal)
        if (onSuccess) {
          await onSuccess();
        }
      },
    },
  });

  const form = useForm<TArchiveTicketValidation>({
    resolver: zodResolver(ArchiveTicketValidationSchema),
    mode: 'onChange',
    defaultValues: {
      spaceId: '',
      ticketId: '',
    },
  });

  // HELPERS
  /*
    Separate loading form and loading mutation
   */
  const isOpen = useMemo(() => {
    return modalActions.checkIsOpenModal('ArchiveTicketModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationArchiveTicket.isPending || form.formState.isSubmitting;
  }, [mutationArchiveTicket, form.formState.isSubmitting]);

  const isLoadingForm = useMemo(() => {
    return mutationArchiveTicket.isPending || form.formState.isSubmitting;
  }, [mutationArchiveTicket, form.formState.isSubmitting]);

  // EFFECTS
  useEffect(() => {
    if (ticketId) {
      form.reset({
        spaceId: spaceId,
        ticketId: ticketId,
      });
    }
  }, [ticketId, spaceId, form]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('ArchiveTicketModal');
  }, [modalActions]);

  const handleCancelModal = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  const handleSubmitForm = useCallback(
    (data: TArchiveTicketValidation) => {
      mutationArchiveTicket.mutate({
        spaceId: data?.spaceId,
        ticketId: data?.ticketId,
      });
    },
    [mutationArchiveTicket]
  );

  return {
    // FORM
    form,
    isOpen,
    isDisabledForm,
    isLoadingForm,

    // HANDLERS
    handleCancelModal,
    handleCloseModal,
    handleSubmitForm,
  };
}
